import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAmplitude } from '~/utils/hooks';

const isApple = () => {
  if ('userAgentData' in navigator) {
    const platform = (
      navigator as Navigator & { userAgentData: { platform: string } }
    ).userAgentData.platform;
    return /mac|ios/i.test(platform);
  }
  return /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent);
};

interface AppDownloadModalProps {
  appUrl: string;
  onDismiss: () => void;
  onContinueInBrowser: () => void;
}

export const AppDownloadModal: React.FC<AppDownloadModalProps> = ({
  appUrl,
  onDismiss,
  onContinueInBrowser,
}) => {
  const { t } = useTranslation();
  const sendEvent = useAmplitude();
  const ios = isApple();
  const storeBadge = ios ? '/img/app-store.png' : '/img/google-play.png';
  const storeBadgeAlt = ios
    ? 'Download on the App Store'
    : 'Get it on Google Play';

  useEffect(() => {
    sendEvent('mainpage_switch_screen_load');
  }, []);

  const handleOpenApp = () => {
    sendEvent('mainpage_switch_screen_click');
    onDismiss();
    window.open(appUrl, '_blank');
  };

  const handleStoreClick = () => {
    sendEvent('mainpage_switch_screen_click');
    onDismiss();
  };

  const handleContinueInBrowser = () => {
    sendEvent('mainpage_switch_screen_skip');
    onContinueInBrowser();
  };

  return createPortal(
    <div className="app-download-overlay">
      <div className="app-download-modal-card">
        <h2 className="app-download-modal-title">
          {t('components.appDownload.modalTitle')}
        </h2>

        <div className="app-download-modal-store">
          <a
            href={appUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleStoreClick}
          >
            <img src={storeBadge} alt={storeBadgeAlt} height={40} />
          </a>
        </div>

        <p className="app-download-modal-message">
          {t('components.appDownload.modalMessage')}
        </p>

        <button className="app-download-modal-open-btn" onClick={handleOpenApp}>
          {t('components.appDownload.openApp')}
        </button>

        <button
          className="app-download-modal-continue"
          onClick={handleContinueInBrowser}
        >
          {t('components.appDownload.continueInBrowser')}
        </button>
      </div>
    </div>,
    document.getElementById('modal')!
  );
};
